/**
 * Razorpay checkout, the parts shared by the native SDK and the web
 * checkout.js: how the server's opaque checkoutPayload becomes Razorpay's
 * options, and how a failure is told apart from the payer closing the sheet.
 *
 * The payload keys are the SERVER adapter's contract (keyId, orderId,
 * amountMinor, currency, merchantName, description, prefill*), exactly as the
 * web portal's RazorpayCheckoutAdapter reads them. The callback Razorpay hands
 * back (razorpay_payment_id, razorpay_order_id, razorpay_signature) goes to
 * the server verbatim - it re-signs it before a single row is written.
 */

export const RAZORPAY = 'RAZORPAY';

export const NOT_AVAILABLE =
  'Online payment is not available here. Pay at the school office.';
export const PAYMENT_FAILED = 'The payment did not go through. Nothing was charged.';

/** Thrown when the payer closes the payment sheet - normal, never an error to shout about. */
export class CheckoutCancelledError extends Error {
  readonly cancelled = true;
  constructor() {
    super('Payment cancelled - nothing was taken.');
    this.name = 'CheckoutCancelledError';
  }
}

export const isCheckoutCancelled = (error: unknown): boolean =>
  !!error && (error instanceof CheckoutCancelledError || (error as any).cancelled === true);

/** True when the server named a provider this app can open. */
export const isSupportedProvider = (provider: string | null | undefined): boolean =>
  (provider ?? '').trim().toUpperCase() === RAZORPAY;

/** checkoutPayload -> Razorpay options, key for key as the web adapter maps them. */
export function toRazorpayOptions(payload: Record<string, string>, description?: string) {
  return {
    key: payload.keyId,
    order_id: payload.orderId,
    amount: Number(payload.amountMinor),
    currency: payload.currency,
    name: payload.merchantName,
    description: payload.description ?? description,
    prefill: {
      name: payload.prefillName,
      email: payload.prefillEmail,
      contact: payload.prefillContact,
    },
    // Razorpay retrying inside its own sheet would hand back a payment id for
    // an attempt this screen has stopped waiting on.
    retry: { enabled: false },
  };
}

/** Every value as a string, as the server's gatewayCallback is Record<string,string>. */
export function toGatewayCallback(data: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!data || typeof data !== 'object') { return out; }
  Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null) { return; }
    out[key] = typeof value === 'string' ? value : typeof value === 'object' ? JSON.stringify(value) : String(value);
  });
  return out;
}

/**
 * The native SDK's error event: { code, description }. Code 2 is
 * PAYMENT_CANCELLED on both Android and iOS; Android also sends the cancel as a
 * JSON description ("Payment processing cancelled by user").
 */
export function describeNativeError(data: unknown): { cancelled: boolean; message: string } {
  const code = Number((data as any)?.code);
  let description: string = (data as any)?.description ?? '';
  if (typeof description === 'string' && description.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(description);
      description = parsed?.error?.description ?? parsed?.description ?? description;
    } catch {
      /* keep the raw text */
    }
  }
  const text = typeof description === 'string' ? description : '';
  const cancelled = code === 2 || /cancel/i.test(text);
  return { cancelled, message: text.trim() || PAYMENT_FAILED };
}
